<template>
	<div class="settings-form">
		<h2>Custom Campaign Limits</h2>
		<gutter size="10px" />
		<p>
			Adding a campaign limit will limit the number of active/paused campaigns of a certain vertical and product that a
			buyer can have at any given time. Once the limit is reached, the buyer will need to archive one of their existing
			campaigns in order to create a new one.
		</p>
		<p>The default campaign limit for each type is 10.</p>
		<div class="campaign-limits">
			<template v-for="(campaignLimit, index) in campaign_limits" :key="`campaign_limit_${index}`">
				<div class="campaign-limit flex align-items-center gap-20">
					<div class="details flex align-items-center gap-20">
						<div class="vertical-id">
							<vertical-icon :vertical-id="campaignLimit.vertical_id" />
						</div>
						<div class="type">
							<strong>{{ title(campaignLimit.vertical_id) }}</strong
							><br />
							<div class="sub-value">{{ title(campaignLimit.product) }} Campaign Limit</div>
						</div>
					</div>
					<div class="limit flex align-items-center gap-10">
						<p-input-number v-model="campaignLimit.limit" class="input-align-right" />
						<p-button v-tooltip.top="'Remove this limit'" icon="pi pi-trash" @click="removeCampaignLimit(index)" />
					</div>
				</div>
			</template>
		</div>
		<gutter size="10px" />
		<p-button label="Add Custom Campaign Limit" text icon="pi pi-plus" @click="openAddCampaignLimitModal" />
		<div class="actions gap-10">
			<p-button label="Save Changes" @click="saveChanges()" />
			<p-button label="Reset" text @click="resetCampaignLimits()" />
		</div>
		<p-dialog v-model:visible="show_modal" modal>
			<template #header><strong>Add Custom Campaign Limit</strong></template>
			<p>Select the vertical and product to create a custom campaign limit for...</p>
			<div class="control-group">
				<div class="inner">
					<label class="control-label">Vertical:</label>
					<div class="controls">
						<div class="field">
							<p-dropdown
								v-model="new_limit_options.vertical_id"
								:options="$root.appStore.options.verticals"
								option-label="label"
								option-value="value"
								placeholder="Select a Vertical"
							/>
							<div v-if="v$.new_limit_options.vertical_id.$error" class="validation-error">
								{{ v$.new_limit_options.vertical_id.$errors[0].$message }}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="control-group">
				<div class="inner">
					<label class="control-label">Product:</label>
					<div class="controls">
						<div class="field">
							<p-dropdown
								v-model="new_limit_options.product"
								:options="$root.appStore.options.products"
								option-label="label"
								option-value="value"
								placeholder="Select a Product Type"
							/>
							<div v-if="v$.new_limit_options.product.$error" class="validation-error">
								{{ v$.new_limit_options.product.$errors[0].$message }}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="v$.existingLimit.$error" class="validation-error">
				{{ v$.existingLimit.$errors[0].$message }}
			</div>
			<template #footer>
				<p-button label="Cancel" text @click="show_modal = false" />
				<p-button icon="pi pi-plus" label="Add Limit" @click="addCampaignLimit" />
			</template>
		</p-dialog>
	</div>
</template>

<script>
import verticalIcon from '@/components/elements/VerticalIcon.vue';
import { title } from '@/lib/Filters';
import { useAccountStore } from '@/stores/account';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { mapStores } from 'pinia';
import { updateAccount } from '@GQL';

export default {
	name: 'CampaignLimits',
	components: {
		verticalIcon,
	},
	setup() {
		return {
			v$: useVuelidate(),
		};
	},
	data() {
		return {
			save_loading: false,
			campaign_limits: [],
			new_limit_options: {
				vertical_id: null,
				product: null,
			},
			show_modal: false,
			changed: false,
		};
	},
	computed: {
		accountId() {
			if (this.$route.params.account_id) {
				return this.$route.params.account_id;
			} else if (this.$route.params.parent_account_id) {
				return this.$route.params.parent_account_id;
			} else {
				return this.sessionStore.account.id;
			}
		},
		...mapStores(useAccountStore),
	},
	validations() {
		return {
			new_limit_options: {
				vertical_id: {
					required: helpers.withMessage('A vertical ID is required', required),
				},
				product: {
					required: helpers.withMessage('A product is required', required),
				},
			},
			existingLimit: {
				exists: helpers.withMessage('A custom limit for that vertical and product already exists', (value) => {
					const existing_limit = this.campaign_limits.find((campaign_limit) => {
						return (
							campaign_limit.vertical_id === this.new_limit_options.vertical_id &&
							campaign_limit.product === this.new_limit_options.product
						);
					});

					return !existing_limit;
				}),
			},
		};
	},
	mounted() {
		this.resetCampaignLimits();
	},
	methods: {
		title,
		openAddCampaignLimitModal() {
			this.v$.$reset();
			this.new_limit_options = {
				vertical_id: null,
				product: null,
			};
			this.show_modal = true;
		},
		async addCampaignLimit() {
			const is_valid = await this.v$.$validate();
			if (is_valid) {
				this.campaign_limits.push({
					vertical_id: this.new_limit_options.vertical_id,
					product: this.new_limit_options.product,
					limit: 10,
				});

				this.show_modal = false;
				this.new_limit_options = {
					vertical_id: null,
					product: null,
				};
			}
		},
		removeCampaignLimit(index) {
			this.campaign_limits.splice(index, 1);
		},
		resetCampaignLimits() {
			this.campaign_limits = this.accountStore.account.settings.campaign_limits || [];
		},
		async saveChanges() {
			this.save_loading = true;
			try {
				await updateAccount(this.accountId, {
					settings: {
						campaign_limits: this.campaign_limits,
					},
				});
				this.accountStore.account.settings.campaign_limits = this.campaign_limits;
				this.$toast.add({
					severity: 'success',
					summary: 'Successfully saved changes',
					life: 3000,
				});
			} catch (err) {
				this.$toast.add({
					severity: 'error',
					summary: 'Unable to save changes',
					detail: err.message,
					life: 5000,
				});
			} finally {
				this.save_loading = false;
			}
		},
	},
};
</script>

<style lang="less" scoped>
.campaign-limit {
	border: 1px solid var(--gray-15);
	border-radius: 5px;
	box-shadow: 0 2px 0 var(--gray-10);
	justify-content: space-between;
	margin-bottom: 1em;
	padding: 20px;

	&:last-child {
		margin-bottom: 0;
	}

	.type {
		line-height: 1.125em;
	}

	.limit {
		flex: 0 0 120px;
		justify-self: end;
	}
}
</style>
